import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * "Install app" affordance.
 *
 * Chrome and Edge fire `beforeinstallprompt`, which is the only way to open the
 * real install dialog — a button that just tells people to use the browser menu
 * is worse than no button. iOS Safari never fires it and has no programmatic
 * install, so there it falls back to naming the Share → Add to Home Screen
 * steps, which is the only route Apple offers.
 *
 * Nothing renders once the app is already running installed.
 */
export default function InstallPWA() {
  const [prompt, setPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("trellure_install_dismissed") === "1",
  );

  const standalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

  const isIos =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !/crios|fxios/i.test(navigator.userAgent);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => setPrompt(null));
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (standalone || dismissed) return null;
  if (!prompt && !isIos) return null;

  const dismiss = () => {
    localStorage.setItem("trellure_install_dismissed", "1");
    setDismissed(true);
  };

  const install = async () => {
    if (isIos) {
      setShowIosHint((v) => !v);
      return;
    }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setPrompt(null);
  };

  return (
    <div className="fixed bottom-28 left-1/2 -translate-x-1/2 z-30 w-[min(92vw,26rem)]">
      <div className="rounded-2xl border border-border bg-card/95 backdrop-blur shadow-2xl px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-300 to-brand-600 flex items-center justify-center shrink-0">
            <Download className="w-4 h-4 text-brand-950" strokeWidth={2.4} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight">Install Trellure</p>
            <p className="text-xs text-muted-foreground leading-tight">
              Add it to your home screen and listen like an app.
            </p>
          </div>
          <button
            onClick={install}
            className="h-8 px-3 rounded-full bg-brand-500 hover:bg-brand-400 text-brand-950 text-xs font-semibold transition-colors shrink-0"
          >
            Install
          </button>
          <button onClick={dismiss} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        {showIosHint && (
          <p className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
            On iPhone: tap the Share button, then <span className="text-foreground">Add to Home Screen</span>.
          </p>
        )}
      </div>
    </div>
  );
}
